import React, { useState, useEffect, useRef, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { useChatContext } from '../contexts/ChatContext';
import { useReport } from '../contexts/ReportContext';
import { useNotification } from '../contexts/NotificationContext';
import { TestResultAPI } from '../api/testResult';
import Navbar from '../components/Navbar';
import ChatArea from '../components/ChatArea';
import ReportArea from '../components/ReportArea';
import NotificationContainer from '../components/NotificationContainer';
import LoginModal from '../components/LoginModal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ProjectHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.surface}, ${({ theme }) => theme.colors.surfaceSecondary});
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 1rem;
  text-align: center;
`;

const ProjectName = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  padding: 1rem;
  gap: 1rem;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    padding: 0.5rem;
  }
`;

const ChatSection = styled.div`
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 1 1 100%;
    max-width: 100%;
  }
`;

const ReportSection = styled.div`
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 1 1 100%;
    max-width: 100%;
  }
`;

// --- Logger Utility ---
const IS_DEV = process.env.NODE_ENV === 'development';
const logger = {
  info: (msg: string, ...args: any[]) => IS_DEV && console.info(msg, ...args),
  warn: (msg: string, ...args: any[]) => IS_DEV && console.warn(msg, ...args),
  error: (msg: string, ...args: any[]) => IS_DEV && console.error(msg, ...args),
  log: (msg: string, ...args: any[]) => IS_DEV && console.log(msg, ...args),
};

// --- Helpers ---
const safeParseJSON = (input: any): any => {
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input);
  } catch {
    return input;
  }
};

const formatTestResultToStandardFormat = (testResultData: any): any => {
  try {
    // Determine sources
    const resultData = testResultData?.script_test_run_result || testResultData;
    // Critical Fix: Check root level first for test_run_test_case
    const testCaseNode = testResultData?.test_run_test_case || testResultData?.script_test_run_result?.test_run_test_case;

    // Execution Data
    const date_time_occurrence = resultData?.executed_at ? String(resultData.executed_at) : '';
    const issue_test_execution_environment = testResultData?.test_run_execution_environment?.name 
      ? String(testResultData.test_run_execution_environment.name) 
      : '';

    // Test Case Data parsing
    let testCaseFields = {
      prerequisite: '',
      primary_item: '',
      secondary_item: '',
      scenario_group: '',
      expected_result: '',
      operating_procedure: ''
    };

    if (testCaseNode?.value) {
      const parsedValue = safeParseJSON(testCaseNode.value);
      testCaseFields = {
        prerequisite: parsedValue?.prerequisite ? String(parsedValue.prerequisite) : '',
        primary_item: parsedValue?.primary_item ? String(parsedValue.primary_item) : '',
        secondary_item: parsedValue?.secondary_item ? String(parsedValue.secondary_item) : '',
        scenario_group: parsedValue?.scenario_group ? String(parsedValue.scenario_group) : '',
        expected_result: parsedValue?.expected_result ? String(parsedValue.expected_result) : '',
        operating_procedure: parsedValue?.verification_procedure ? String(parsedValue.verification_procedure) : '',
      };
    }

    // AI Analysis Value
    const value = resultData?.value ? safeParseJSON(resultData.value) : {};
    const comment = resultData?.comment ? String(resultData.comment) : '';

    return {
      date_time_occurrence,
      issue_test_execution_environment,
      comment,
      value,
      ...testCaseFields
    };
  } catch (error) {
    logger.error('Error formatting test result:', error);
    return testResultData;
  }
};

const createInitialChatMessage = (testResultData: any): string => {
  const formattedData = formatTestResultToStandardFormat(testResultData);
  logger.info('📊 Test Result formatted Data:', formattedData);
  return `Please generate an incident report based on the following test result data:\n\n${JSON.stringify(formattedData, null, 2)}`;
};

// --- Custom Hook: Deep Link Logic (Business Logic Layer) ---
const useDeepLinkHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { currentProject, projects, setCurrentProject, isLoading: projectsLoading } = useProject();
  const { sendMessage, resetSession } = useChatContext();
  const { setIsAutoTriggeringFromUrl } = useReport();
  const { addNotification } = useNotification();
  
  const hasProcessed = useRef(false);
  const isLoading = authLoading || projectsLoading;

  // Encapsulated Trigger Logic
  const triggerChat = useCallback(async (projectId: string, message: string, workspaceId?: string) => {
    // 500ms delay to ensure Context propagation
    setTimeout(async () => {
      try {
        await resetSession(projectId);
        // Set flag when auto-triggering from URL
        // Backend will decide whether to return overview data based on content
        // If content is empty/minimal, backend won't return overview → hasSummaryData = false → won't show
        // If content has data, backend returns overview → hasSummaryData = true → will show
        setIsAutoTriggeringFromUrl(true);
        const sent = await sendMessage(projectId, message, undefined, workspaceId);
        if (!sent) {
          // 送れなかったら URL（project_id・content など）を残し、リロードで同じ分析を送り直せるようにする
          logger.warn('⚠️ Deep link message was not sent; keeping URL params');
          setIsAutoTriggeringFromUrl(false);
          return;
        }
        setSearchParams({}, { replace: true });
        // Flag isAutoTriggeringFromUrl is cleared by useChat when messages.length > 0
        logger.info('✅ Deep link processed successfully');
      } catch (error) {
        logger.error('❌ Failed to process trigger:', error);
        setIsAutoTriggeringFromUrl(false);
        addNotification({
          type: 'error',
          title: '処理エラー',
          message: error instanceof Error ? error.message : 'チャットの開始に失敗しました',
        });
      }
    }, 500);
  }, [resetSession, sendMessage, setSearchParams, addNotification, setIsAutoTriggeringFromUrl]);

  useEffect(() => {
    // 1. Guard Clauses
    if (isLoading || !isAuthenticated || projects.length === 0 || hasProcessed.current) {
      return;
    }

    const testResultId = searchParams.get('test_result_id');
    const content = searchParams.get('content');
    const projectIdParam = searchParams.get('project_id');

    if (!testResultId && !content) {
      // project_id のみの場合はプロジェクト切り替えのみ実施（分析は開始しない）
      if (projectIdParam) {
        hasProcessed.current = true;
        const targetProject = projects.find(p => p.id === projectIdParam);
        if (targetProject && (!currentProject || currentProject.id !== projectIdParam)) {
          setCurrentProject(targetProject);
        }
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (!projectIdParam) {
      logger.warn('⚠️ project_id missing in URL');
      addNotification({ type: 'error', title: 'エラー', message: 'project_id が不足しています' });
      setSearchParams({}, { replace: true });
      return;
    }

    // Lock execution
    hasProcessed.current = true;
    logger.info(`🚀 Starting Deep Link processing. Mode: ${testResultId ? 'TestResultID' : 'Content'}`);

    // 2. Validate Project Access
    const targetProject = projects.find(p => p.id === projectIdParam);
    if (!targetProject) {
      logger.error(`❌ Access denied for project ${projectIdParam}`);
      addNotification({ type: 'error', title: '権限エラー', message: 'プロジェクトへのアクセス権限がありません' });
      setSearchParams({}, { replace: true });
      return;
    }

    // 3. Switch Project Context if needed
    if (!currentProject || currentProject.id !== projectIdParam) {
      setCurrentProject(targetProject);
    }

    // 4. Mode A: Test Result ID (API Fetch)
    if (testResultId) {
      const workspaceId = targetProject.workspace_id;
      if (!workspaceId) {
        addNotification({ type: 'error', title: '設定エラー', message: 'ワークスペースIDが見つかりません' });
        return;
      }

      TestResultAPI.getTestResult(testResultId, workspaceId, projectIdParam)
        .then(response => {
          if (!response.success || !response.data) throw new Error(response.error || 'API Error');
          
          // Normalize Data
          let rawData = response.data;
          if (response.data.script_test_result_details?.[0]) rawData = response.data.script_test_result_details[0];
          else if (response.data.test_result) rawData = response.data.test_result;

          if (!rawData) throw new Error('Empty test result data');

          logger.info('📊 Fetched Data:', rawData);
          const message = createInitialChatMessage(rawData);
          triggerChat(projectIdParam, message, workspaceId);
        })
        .catch(err => {
          logger.error('❌ API Fetch Failed:', err);
          addNotification({ type: 'error', title: '取得エラー', message: 'テスト結果の取得に失敗しました' });
          setSearchParams({}, { replace: true });
        });
    } 
    // 5. Mode B: Raw Content
    else if (content) {
      try {
        const decoded = decodeURIComponent(content);
        triggerChat(projectIdParam, decoded, targetProject.workspace_id);
      } catch (e) {
        logger.error('Decoding failed', e);
        triggerChat(projectIdParam, content, targetProject.workspace_id); // Fallback to raw
      }
    }

  }, [
    isLoading, 
    isAuthenticated, 
    projects, 
    currentProject, 
    searchParams, 
    setSearchParams, 
    setCurrentProject, 
    addNotification, 
    triggerChat
  ]);

  return { isLoading };
};

// --- Main Component ---
const ChatPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, serviceUnavailable } = useAuth();
  const { currentProject } = useProject();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Use the custom hook for logic
  const { isLoading: hookLoading } = useDeepLinkHandler();

  const isLoading = authLoading || hookLoading;

  // 1. Handle Authentication Gates
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !serviceUnavailable) {
      setShowLoginModal(true);
    }
  }, [isLoading, isAuthenticated, serviceUnavailable]);

  const handleLoginSuccess = () => setShowLoginModal(false);

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div>読み込み中...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      
      <MainContent>
        <ProjectHeader>
          <ProjectName>
            {currentProject ? currentProject.name : 'プロジェクト未選択'}
          </ProjectName>
        </ProjectHeader>

        {serviceUnavailable && (
          <div role="alert" style={{ padding: '8px 16px', color: '#b91c1c' }}>
            サービスに接続できません。しばらくしてから再度お試しください
          </div>
        )}

        <ContentContainer>
          <ChatSection>
            <ChatArea />
          </ChatSection>
          
          <ReportSection>
            <ReportArea />
          </ReportSection>
        </ContentContainer>
      </MainContent>

      <NotificationContainer />
      
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </PageContainer>
  );
};

export default ChatPage;